import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import { useLocation } from '@reach/router';
import { useGlobalState } from './global-context';
import { cn, getCleanPathname } from "../lib/helpers";
import MenuItem from "./menu-item";
import LinkElem from "./link";
import Button from "./button";
import Svg from 'react-inlinesvg';

import * as styles from "../styles/components/header.module.css";

interface ImageNode {
  asset?: {
    _id?: string;
    url?: string;
  };
  alt?: string;
}

interface LinkNode {
  _key?: string;
  url?: string;
  blank?: boolean;
  text?: string;
}

interface SubmenuLink extends LinkNode {
  image?: ImageNode;
}

interface CtaLink extends LinkNode {
  primaryButton?: boolean;
}

interface SubmenuBlock {
  title?: string;
  menu?: SubmenuLink[];
}

interface Submenu {
  primary?: SubmenuBlock;
  link?: LinkNode;
  secondary?: SubmenuBlock;
}

interface SiteSettings {
  title?: string;
  logo?: ImageNode;
}

interface MainMenu extends LinkNode {
  submenu?: Submenu;
  defaultImage?: ImageNode;
}

interface HeaderQueryData {
  site?: SiteSettings;
  nav?: {
    mainMenu?: MainMenu[];
    headerCtas?: CtaLink[];
  };
  contact?: {
    phoneTf?: LinkNode;
    email?: LinkNode;
  }
}

const Header: React.FC = ({ pageLoaded }) => {
  const data = useStaticQuery<HeaderQueryData>(graphql`
    query HeaderQuery {
      site: sanitySettings {
        title
        logo {
          asset {
            url
          }
        }
      }

      nav: sanityNav {
        mainMenu {
          _key
          blank
          text
          url
          submenu {
            primary {
              title
              menu {
                _key
                blank
                text
                url
                image {
                  asset {
                    _id
                  }
                  alt
                }
              }
            }
            link {
              blank
              text
              url
            }
            secondary {
              title
              menu {
                _key
                blank
                text
                url
                image {
                  asset {
                    _id
                  }
                  alt
                }
              }
            }
          }
          defaultImage {
            asset {
              _id
            }
            alt
          }
        }
        headerCtas {
          _key
          blank
          text
          url
          primaryButton
        }
      }

      contact: sanityContact {
        phoneTf {
          text
          url
        }
        email {
          text
          url
        }
      }
    }
  `);

  // Destructure your query data for easier access
  const { site, nav, contact } = data || {};
  const { title, logo } = site || {};
  const { mainMenu, headerCtas } = nav || {};
  const { phoneTf, email } = contact || {};

  const { isMobile } = useGlobalState();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const location = useLocation();

  // Ref to hold timeout IDs
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleShowMenu = () => {
    setShowMenu(true);
    document.body.classList.add(styles.menuOpen);
  }

  const handleHideMenu = useCallback((e: React.MouseEvent<HTMLAnchorElement> | null) => {
    let delay = 600;

    if(getCleanPathname(e?.target?.getAttribute("href")) === location.pathname || e?.target?.tagName === "BUTTON" || !e) {
      delay = 0;
    }

    setTimeout(() => {
      setShowMenu(false);
      document.body.classList.remove(styles.menuOpen, 'header-no-transition');
    }, delay);
  }, [isMobile]);

  // desktop func
  useEffect(() => {
    const navItems = document.querySelectorAll(`.${styles.mainMenu} > li`);
    const navElement = document.querySelector( `.${styles.mainMenuBlock}`);

    let prevHadDropdown = null;

    function handleMouseEnter() {
      const dropdown = this.querySelector('ul');
      const hasDropdown = Boolean(dropdown);

      if (hasDropdown) {
        if (prevHadDropdown === null || prevHadDropdown !== hasDropdown) {
          document.body.classList.remove('header-no-transition');
        } else {
          document.body.classList.add('header-no-transition');
        }
      }
      else {
        document.body.classList.remove('header-no-transition');
      }

      prevHadDropdown = hasDropdown;
    }

    function handleNavMouseLeave() {
      document.body.classList.remove('header-no-transition');
      prevHadDropdown = null; // reset the previous state if needed
    }

    navItems.forEach((item) => {
      item.addEventListener('mouseenter', handleMouseEnter);
    });

    if (navElement) {
      navElement.addEventListener('mouseleave', handleNavMouseLeave);
    }

    // Clean up event listeners on unmount
    return () => {
      navItems.forEach((item) => {
        item.removeEventListener('mouseenter', handleMouseEnter);
      });

      if (navElement) {
        navElement.removeEventListener('mouseleave', handleNavMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    handleHideMenu();
  }, [handleHideMenu]);

  return (
    <header className={styles.root}>
      {logo?.asset && (
        <div className={styles.branding}>
          <Link to="/" onClick={handleHideMenu}>
            <Svg 
              src={logo.asset.url}
              title={title}
              description={title + " logo"}
              uniquifyIDs={true}
            />
          </Link>
        </div>
      )}

      {mainMenu?.length > 0 && (
        <nav className={styles.mainMenuBlock}>
          <ul className={cn(styles.menu, styles.mainMenu)}>
            {mainMenu.map(node => (
              <MenuItem 
                key={node._key}
                node={node} 
                onClick={handleHideMenu} 
                showMenu={showMenu}
                pageLoaded={pageLoaded}
              />
            ))}
          </ul>

          {(headerCtas?.length > 0 || email?.url || phoneTf?.url) && (
            <div className={styles.mMenuBlock}>
              {(email?.url || phoneTf?.url) && (
                <div className={cn("body-small", styles.contact)}>
                  {email?.url && (
                    <div><LinkElem node={email} noStyle={true} contact={true} onClick={handleHideMenu} /></div>
                  )}
                  {phoneTf?.url && (
                    <div><LinkElem node={phoneTf} noStyle={true} contact={true} onClick={handleHideMenu} /></div>
                  )}
                </div>
              )}

              {headerCtas?.length > 0 && (
                <ul className={styles.headerCtas}>
                  {headerCtas.map(node => (
                    <li key={node._key}>
                      <Button node={node} noStyle={true} primaryCta={node.primaryButton} onClick={handleHideMenu} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </nav>
      )}

      {headerCtas?.length > 0 && (
        <nav className={styles.headerCtasBlock}>
          <ul className={cn(styles.menu, styles.headerCtas)}>
            {headerCtas.map(node => (
              <li key={node._key}>
                <Button node={node} noStyle={true} primaryCta={node.primaryButton} onClick={handleHideMenu} />
              </li>
            ))}
          </ul>
        </nav>
      )}

      <button className={styles.menuToggle} onClick={showMenu ? handleHideMenu : handleShowMenu}>
        <span className="sr-only">Toggle Menu</span>
      </button>
    </header>
  )
};

export default Header;
